/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: How to use section - Remove Subtitles, Watermarks, Logos from Videos
 */
import Image from 'next/image';
import React from 'react';

interface Step {
  number: number;
  title: string;
  description: string;
}

const steps: Step[] = [
  {
    number: 1,
    title: 'Upload Your Video',
    description:
      'Upload the video you want to clean. FalcoCut supports common formats like MP4 and MOV, so you can get started without converting anything.',
  },
  {
    number: 2,
    title: 'Mark the Removal Area',
    description:
      "Use the selection tool to highlight the area you want to erase. Whether it's subtitles, watermarks, logos, or other unwanted text, ensure the removal box fully covers the area for best results.",
  },
  {
    number: 3,
    title: 'Preview the Clean Result',
    description:
      'Click Start to let FalcoCut rebuild the pixels behind the text or logo for a smooth, natural finish. Review the preview to ensure all unwanted elements are fully removed.',
  },
  {
    number: 4,
    title: 'Export Your Clip',
    description:
      'Once everything looks right, export your cleaned video in high quality. Your clip is now ready to share, reuse, or edit further.',
  },
];

const StepItem: React.FC<Step> = ({ number, title, description }) => {
  return (
    <div className="flex gap-4 pad:gap-5">
      {/* Step Number */}
      <div className="flex-shrink-0 w-10 h-10 pad:w-12 pad:h-12 rounded-full bg-[#6366F1] flex items-center justify-center">
        <span className="text-white font-bold text-lg pad:text-xl">{number}</span>
      </div>

      {/* Step Content */}
      <div className="flex-1 pt-1">
        <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm pad:text-base text-gray-600 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
};

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          How to Remove Subtitles, Watermarks, Logos, and Other Unwanted Text from Videos
          Easily
        </h2>

        {/* Content - Image and Steps */}
        <div className="flex flex-col pad:flex-row gap-8 pad:gap-12 sm:gap-16 items-center">
          {/* Left - Image */}
          <div className="w-full pad:w-1/2">
            <div className="relative w-full rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3]">
              <Image
                src="https://assets.falcocut.ai/ai-removal/how-to-remove-subtitles-watermarks-logos-from-video.webp"
                alt="How to remove subtitles, watermarks, logos from video"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>

          {/* Right - Steps */}
          <div className="w-full pad:w-1/2 flex flex-col gap-6 pad:gap-8">
            {steps.map((step) => (
              <StepItem
                key={step.number}
                number={step.number}
                title={step.title}
                description={step.description}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
